import React, { useEffect, useState } from "react";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  (process.env.NODE_ENV === "development"
    ? "http://localhost:5001"
    : "https://kaushik-olympics-backend.mshik3.workers.dev");

interface TeamsState {
  names: { A: string; B: string };
  members: { A: string[]; B: string[] };
}

interface Props {
  participants: string[];
  teams?: TeamsState;
  onTeamsChange?: (teams: TeamsState) => void;
}

const TeamAdmin: React.FC<Props> = ({
  participants,
  teams: propTeams,
  onTeamsChange,
}) => {
  const [teams, setTeams] = useState<TeamsState>({
    names: { A: "Team A", B: "Team B" },
    members: { A: [], B: [] },
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [recentlyMoved, setRecentlyMoved] = useState<Set<string>>(new Set());

  // Keep local state in sync with parent-provided teams
  useEffect(() => {
    if (propTeams) {
      setTeams(propTeams);
      setLoading(false);
    }
  }, [propTeams]);

  useEffect(() => {
    if (propTeams) return; // Parent controls state; no need to fetch
    const fetchTeams = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/teams`);
        const data = await res.json();
        setTeams(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchTeams();
  }, [propTeams]);

  // Client-side fallback: if no team members are provided from server,
  // derive a balanced split from participants so the UI shows names.
  useEffect(() => {
    if (loading) return;
    const a = teams.members.A || [];
    const b = teams.members.B || [];
    if (a.length === 0 && b.length === 0 && participants.length > 0) {
      const unique = Array.from(new Set(participants));
      const teamA: string[] = [];
      const teamB: string[] = [];
      for (let i = 0; i < unique.length; i++) {
        if (teamA.length <= teamB.length) teamA.push(unique[i]);
        else teamB.push(unique[i]);
      }
      const next = { ...teams, members: { A: teamA, B: teamB } };
      setTeams(next);
      onTeamsChange && onTeamsChange(next);
    }
  }, [loading, participants, teams, onTeamsChange]);

  // Removed unused assignments map to satisfy no-unused-vars

  const setMemberTeam = (name: string, team: "A" | "B") => {
    setTeams((prev) => {
      const A = new Set(prev.members.A);
      const B = new Set(prev.members.B);
      A.delete(name);
      B.delete(name);
      if (team === "A") A.add(name);
      if (team === "B") B.add(name);
      const next = {
        ...prev,
        members: { A: Array.from(A), B: Array.from(B) },
      };
      onTeamsChange && onTeamsChange(next);
      return next;
    });
  };

  const moveToOppositeTeam = (name: string, currentTeam: "A" | "B") => {
    const dest = currentTeam === "A" ? "B" : "A";
    setMemberTeam(name, dest);
    setRecentlyMoved((prev) => {
      const next = new Set(prev);
      next.add(name);
      return next;
    });
    setTimeout(() => {
      setRecentlyMoved((prev) => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
    }, 900);
  };

  const saveNames = async () => {
    setSaving(true);
    try {
      await fetch(`${API_BASE}/api/teams/names`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(teams.names),
      });
      window.dispatchEvent(new Event("teams-updated"));
      alert("Team names saved");
    } catch (e) {
      alert("Failed to save team names");
    } finally {
      setSaving(false);
    }
  };

  const saveMembers = async () => {
    setSaving(true);
    try {
      await fetch(`${API_BASE}/api/teams/members`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(teams.members),
      });
      window.dispatchEvent(new Event("teams-updated"));
      alert("Team members saved");
    } catch (e) {
      alert("Failed to save team members");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="sport-card">
        <div className="sport-card-header">
          <h2>Teams</h2>
        </div>
        <div>Loading...</div>
      </div>
    );

  return (
    <div className="sport-card">
      <div className="sport-card-header">
        <h2>Teams</h2>
        <div className="sport-actions">
          <button onClick={saveNames} disabled={saving}>
            Save Names
          </button>
          <button onClick={saveMembers} disabled={saving}>
            Save Members
          </button>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 8,
            }}
          >
            <label style={{ width: 70 }}>Team A</label>
            <input
              value={teams.names.A}
              onChange={(e) =>
                setTeams((t) => {
                  const next = {
                    ...t,
                    names: { ...t.names, A: e.target.value },
                  };
                  onTeamsChange && onTeamsChange(next);
                  return next;
                })
              }
            />
          </div>
          <div>
            {(teams.members.A || []).map((name) => (
              <div
                key={name}
                className={`rank-item${
                  recentlyMoved.has(name) ? " just-moved" : ""
                }`}
                style={{ gridTemplateColumns: "1fr 36px" }}
              >
                <div className="rank-name">{name}</div>
                <button
                  className="swap-btn"
                  title={`Move to ${teams.names.B}`}
                  aria-label={`Move ${name} to ${teams.names.B}`}
                  onClick={() => moveToOppositeTeam(name, "A")}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 7h11l-3.5-3.5 1.4-1.4L22.8 9l-6.9 6.9-1.4-1.4L18 11H7V7z"
                      fill="currentColor"
                    />
                    <path
                      d="M17 17H6l3.5 3.5-1.4 1.4L1.2 15l6.9-6.9 1.4 1.4L6 13h11v4z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 8,
            }}
          >
            <label style={{ width: 70 }}>Team B</label>
            <input
              value={teams.names.B}
              onChange={(e) =>
                setTeams((t) => {
                  const next = {
                    ...t,
                    names: { ...t.names, B: e.target.value },
                  };
                  onTeamsChange && onTeamsChange(next);
                  return next;
                })
              }
            />
          </div>
          <div>
            {(teams.members.B || []).map((name) => (
              <div
                key={name}
                className={`rank-item${
                  recentlyMoved.has(name) ? " just-moved" : ""
                }`}
                style={{ gridTemplateColumns: "1fr 36px" }}
              >
                <div className="rank-name">{name}</div>
                <button
                  className="swap-btn"
                  title={`Move to ${teams.names.A}`}
                  aria-label={`Move ${name} to ${teams.names.A}`}
                  onClick={() => moveToOppositeTeam(name, "B")}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 7h11l-3.5-3.5 1.4-1.4L22.8 9l-6.9 6.9-1.4-1.4L18 11H7V7z"
                      fill="currentColor"
                    />
                    <path
                      d="M17 17H6l3.5 3.5-1.4 1.4L1.2 15l6.9-6.9 1.4 1.4L6 13h11v4z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamAdmin;
